import { errorName, errorMessage, isKnownErrorCode } from '@academy/config'

export { errorName, errorMessage, isKnownErrorCode }

/**
 * Friendly failure text for an API error envelope. Prefers the catalog's
 * user-facing message for the code; falls back to the server-provided message
 * (which may be more specific) and finally a generic line. Use this anywhere we
 * show an API failure to the user.
 */
export function friendlyError(err: { code?: string; message?: string } | null | undefined): string {
  if (!err) return errorMessage('INTERNAL_ERROR')
  if (err.code && isKnownErrorCode(err.code)) {
    // A specific server message beats the generic catalog default when present
    // and different; otherwise use the catalog message.
    return err.message && err.message.trim().length > 0 ? err.message : errorMessage(err.code)
  }
  return err.message ?? errorMessage('INTERNAL_ERROR')
}

/** Short label for a code (e.g. "Number not allowed"), for chips/toasts. */
export function friendlyErrorName(code: string | undefined): string {
  return code ? errorName(code) : 'Error'
}

/** Human labels for the raw field paths a Zod flatten() reports. */
const FIELD_LABELS: Record<string, string> = {
  fullName: 'Name',
  classIds: 'Class',
  dateOfBirth: 'Date of birth',
  schoolName: 'School name',
  address: 'Address',
  examBoard: 'Exam board',
  parents: 'Parent contacts',
  photoUrl: 'Photo',
  feePlan: 'Fee plan',
  referredByCode: 'Referred-by code',
}

/**
 * Turn a VALIDATION_ERROR envelope's `details` (a Zod flatten:
 * `{ formErrors, fieldErrors }`) into a readable, field-labelled sentence, e.g.
 * "Date of birth: expected YYYY-MM-DD. Class: at least one is required." Returns
 * null when there are no field/form errors to surface, so callers can fall back
 * to the generic message.
 */
export function formatValidationDetails(
  details: { formErrors?: string[]; fieldErrors?: Record<string, string[]> } | undefined,
): string | null {
  if (!details) return null
  const parts: string[] = []
  for (const [field, msgs] of Object.entries(details.fieldErrors ?? {})) {
    if (!msgs || msgs.length === 0) continue
    const label = FIELD_LABELS[field] ?? field
    parts.push(`${label}: ${msgs.join(', ')}`)
  }
  for (const msg of details.formErrors ?? []) {
    if (msg && msg.trim().length > 0) parts.push(msg)
  }
  return parts.length > 0 ? parts.join('. ') : null
}
